/**
 * useRealtimeRiskAlerts - Real-time risk alert and violation streaming via WebSocket
 * Streams risk limit breaches, position alerts, and compliance violations
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';

// =============================================================================
// TYPES
// =============================================================================

export interface RealtimeRiskAlert {
  alertId: string;
  type: 'limit_breach' | 'position_warning' | 'margin_call' | 'drawdown' | 'volatility' | 'concentration';
  severity: 'info' | 'warning' | 'critical' | 'emergency';
  title: string;
  message: string;
  metric: string;
  currentValue: number;
  threshold: number;
  symbol?: string;
  portfolio?: string;
  timestamp: string;
  acknowledged: boolean;
}

export interface RealtimeRiskMetric {
  metricId: string;
  name: string;
  category: 'var' | 'exposure' | 'drawdown' | 'leverage' | 'concentration' | 'pnl';
  value: number;
  previousValue: number;
  change: number;
  changePercent: number;
  limit?: number;
  utilizationPercent?: number;
  status: 'normal' | 'warning' | 'critical';
  timestamp: string;
}

export interface RealtimeViolation {
  violationId: string;
  ruleId: string;
  ruleName: string;
  severity: 'minor' | 'major' | 'critical';
  description: string;
  currentValue: number;
  allowedValue: number;
  symbol?: string;
  timestamp: string;
  autoResolved: boolean;
}

export interface UseRealtimeRiskAlertsConfig {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Maximum alerts to buffer */
  maxAlerts?: number;
  /** Minimum severity to report */
  minSeverity?: 'info' | 'warning' | 'critical' | 'emergency';
  /** Callback on new alert */
  onAlert?: (alert: RealtimeRiskAlert) => void;
  /** Callback on metric update */
  onMetricUpdate?: (metric: RealtimeRiskMetric) => void;
  /** Callback on violation */
  onViolation?: (violation: RealtimeViolation) => void;
  /** Enable audio alerts for critical events */
  audioAlerts?: boolean;
}

// =============================================================================
// SEVERITY ORDERING
// =============================================================================

const SEVERITY_ORDER = { info: 0, warning: 1, critical: 2, emergency: 3 } as const;

// =============================================================================
// HOOK
// =============================================================================

export const useRealtimeRiskAlerts = (config: UseRealtimeRiskAlertsConfig = {}) => {
  const {
    autoConnect = true,
    maxAlerts = 100,
    minSeverity = 'info',
    onAlert,
    onMetricUpdate,
    onViolation,
    audioAlerts = false,
  } = config;

  const [alerts, setAlerts] = useState<RealtimeRiskAlert[]>([]);
  const [metrics, setMetrics] = useState<Map<string, RealtimeRiskMetric>>(new Map());
  const [violations, setViolations] = useState<RealtimeViolation[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);

  const callbacksRef = useRef({ onAlert, onMetricUpdate, onViolation });
  callbacksRef.current = { onAlert, onMetricUpdate, onViolation };

  const handleMessage = useCallback((data: any) => {
    if (data.type === 'pong' || data.type === 'ping') return;

    switch (data.type) {
      case 'risk_alert': {
        const alert = data.data as RealtimeRiskAlert;

        // Filter by minimum severity
        if (SEVERITY_ORDER[alert.severity] < SEVERITY_ORDER[minSeverity]) return;

        setAlerts((prev: RealtimeRiskAlert[]) => [alert, ...prev].slice(0, maxAlerts));

        if (!alert.acknowledged) {
          setUnacknowledgedCount((prev: number) => prev + 1);
        }
        if (alert.severity === 'critical' || alert.severity === 'emergency') {
          setCriticalCount((prev: number) => prev + 1);
        }

        // Audio alert for critical/emergency
        if (audioAlerts && (alert.severity === 'critical' || alert.severity === 'emergency')) {
          try {
            const audio = new Audio('/sounds/alert.mp3');
            audio.play().catch(() => { }); // Ignore autoplay errors
          } catch {
            // Audio not available
          }
        }

        callbacksRef.current.onAlert?.(alert);
        break;
      }

      case 'risk_metric': {
        const metric = data.data as RealtimeRiskMetric;
        setMetrics((prev: Map<string, RealtimeRiskMetric>) => {
          const updated = new Map(prev);
          updated.set(metric.metricId, metric);
          return updated;
        });
        callbacksRef.current.onMetricUpdate?.(metric);
        break;
      }

      case 'risk_violation': {
        const violation = data.data as RealtimeViolation;
        setViolations((prev: RealtimeViolation[]) => [violation, ...prev].slice(0, maxAlerts));
        callbacksRef.current.onViolation?.(violation);
        break;
      }
    }
  }, [minSeverity, maxAlerts, audioAlerts]);

  // Private channel: mint short-lived ticket on every connect/reconnect
  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    {
      channel: 'notifications',
      useTicket: true,
      requireTicket: true,
      softFailAuth: false,
      reconnect: { enabled: true, maxAttempts: 15, delay: 500, backoffMultiplier: 1.5 },
      heartbeat: { enabled: true, interval: 15000, message: { type: 'ping' } },
    },
    handleMessage
  );

  // Auto-connect
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => disconnect();
  }, [autoConnect, connect, disconnect]);

  // Subscribe to risk channels when connected
  useEffect(() => {
    if (state.status === 'connected') {
      subscribe('risk_alert', () => { });
      subscribe('risk_metric', () => { });
      subscribe('risk_violation', () => { });

      send({
        type: 'subscribe',
        channels: ['risk_alerts', 'risk_metrics', 'risk_violations'],
      });
    }
  }, [state.status, subscribe, send]);

  // Acknowledge alert
  const acknowledgeAlert = useCallback((alertId: string) => {
    setAlerts((prev: RealtimeRiskAlert[]) =>
      prev.map((a: RealtimeRiskAlert) => a.alertId === alertId ? { ...a, acknowledged: true } : a)
    );
    setUnacknowledgedCount((prev: number) => Math.max(0, prev - 1));
    send({ type: 'acknowledge_alert', alertId });
  }, [send]);

  // Acknowledge all alerts
  const acknowledgeAll = useCallback(() => {
    setAlerts((prev: RealtimeRiskAlert[]) => prev.map((a: RealtimeRiskAlert) => ({ ...a, acknowledged: true })));
    setUnacknowledgedCount(0);
    send({ type: 'acknowledge_all_alerts' });
  }, [send]);

  // Clear alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
    setViolations([]);
    setUnacknowledgedCount(0);
    setCriticalCount(0);
  }, []);

  // Derived state
  const criticalAlerts = alerts.filter((a: RealtimeRiskAlert) => a.severity === 'critical' || a.severity === 'emergency');
  const warningAlerts = alerts.filter((a: RealtimeRiskAlert) => a.severity === 'warning');
  const metricsArray = Array.from(metrics.values()) as RealtimeRiskMetric[];
  const abnormalMetrics = metricsArray.filter((m: RealtimeRiskMetric) => m.status !== 'normal');

  return {
    // Connection
    connectionStatus: state.status,
    isConnected: state.status === 'connected',
    connect,
    disconnect,

    // Data
    alerts,
    criticalAlerts,
    warningAlerts,
    unacknowledgedCount,
    criticalCount,
    metrics: metricsArray,
    abnormalMetrics,
    violations,

    // Actions
    acknowledgeAlert,
    acknowledgeAll,
    clearAlerts,
    send,
  };
};

export default useRealtimeRiskAlerts;
